import { SelectOptionProps } from '@acceso/design-system';

import { useCurrentLanguage } from '@/utils/i18n.utils';

import { useLocations } from '../api/get-locations';

type UseLocationOptionsParams = {
  regionCode?: string | null;
  countryCode?: string | null;
};

export function useLocationOptions({ countryCode, regionCode }: UseLocationOptionsParams = {}) {
  const currentLanguage = useCurrentLanguage();
  const { data: locations = [] } = useLocations();
  const countryOptions: SelectOptionProps<string>[] = locations.map(location => ({
    label: location.labels[currentLanguage],
    value: location.value,
  }));

  const regions = locations
    .filter(location => !countryCode || location.value === countryCode)
    .flatMap(location => location.options ?? []);
  const regionOptions: SelectOptionProps<string>[] = regions.map(location => ({
    label: location.labels[currentLanguage],
    value: location.value,
  }));

  const subregions = regions
    .filter(option => !regionCode || option.value === regionCode)
    .flatMap(location => location.options ?? []);
  const subregionOptions: SelectOptionProps<string>[] = subregions.map(location => ({
    label: location.labels[currentLanguage],
    value: location.value,
  }));

  return {
    countryOptions,
    regionOptions,
    subregionOptions,
  };
}

export function useLocationCountyRegionBySubRegionCode(subregionCode: string): {
  country: SelectOptionProps<string>;
  region: SelectOptionProps<string>;
} {
  const { data: locations = [] } = useLocations();
  const currentLanguage = useCurrentLanguage();

  for (const country of locations) {
    if (!country.options) continue;
    for (const region of country.options) {
      if (!region.options) continue;
      const subregion = region.options.find(sub => sub.value === subregionCode);
      if (subregion) {
        return {
          country: {
            label: country.labels[currentLanguage],
            value: country.value,
          },
          region: {
            label: region.labels[currentLanguage],
            value: region.value,
          },
        };
      }
    }
  }
  return {
    country: { label: '', value: '' },
    region: { label: '', value: '' },
  };
}
